import { type ForwardedRef, useImperativeHandle } from "react";
import type {
  AiScriptSpreadsheetViewerHandle,
  AiScriptSpreadsheetViewerProps,
} from "./ai-script-spreadsheet-viewer-types";
import { useViewerActions } from "./use-viewer-actions";
import { useViewerState } from "./use-viewer-state";
import { emptySelection } from "./utils";

export const useSpreadsheetViewer = (
  props: AiScriptSpreadsheetViewerProps,
  ref: ForwardedRef<AiScriptSpreadsheetViewerHandle>,
) => {
  const state = useViewerState(props);
  const { ui, sheet, comments, history } = state;
  const { selectedCol, selectedRow } = state.selected;
  const { activeSheetValue, changeActiveSheet, clearGridViewport } = sheet;

  useImperativeHandle(ref, () => ({
    clearSelection: () => ui.setGridSelection(emptySelection),
    goToCell: (sheetId, row, col) => {
      if (sheetId && sheetId !== activeSheetValue) changeActiveSheet(sheetId);
      else clearGridViewport();
      ui.setPendingCell({ sheetId, row, col });
    },
    openCommentsAt: (sheetId, row, col) => {
      const selected =
        selectedCol !== null && selectedRow !== null
          ? comments.savedCellCoordinate(selectedCol, selectedRow)
          : null;
      if (
        sheetId === activeSheetValue &&
        selected?.row === row &&
        selected.col === col &&
        selectedCol !== null &&
        selectedRow !== null
      ) {
        clearGridViewport();
        ui.setPendingCell({ sheetId, row, col });
        comments.openCommentCard(selectedCol, selectedRow, false);
        return;
      }
      ui.pendingCommentOpenRef.current = { sheetId, row, col };
      ui.setCommentCard(null);
      if (sheetId && sheetId !== activeSheetValue) changeActiveSheet(sheetId);
      else clearGridViewport();
      ui.setPendingCell({ sheetId, row, col });
    },
    resetHistory: () => history.reset(),
  }));

  const actions = useViewerActions(state, props);

  return { ...state, ...actions };
};

export type SpreadsheetViewer = ReturnType<typeof useSpreadsheetViewer>;
